import { Children, cloneElement, isValidElement, type ReactNode } from 'react'
import type { Langue } from '@tb/shared'
import { useLangueVitrine } from '../../lib/langue'
import { CONTENU_TRADUIT } from './contenu-traduit'

/** Seuls les contenus éditoriaux passent ici, jamais les valeurs saisies,
 * routes, identifiants, événements ou styles. Aucune mutation du DOM.
 * Le rendu FR reste exactement celui des sections validées. */
const PROPS_TEXTE = new Set([
  'children',
  'titre',
  'surtitre',
  'chapeau',
  'description',
  'label',
  'aide',
  'erreur',
  'message',
  'alt',
  'title',
  'placeholder',
  'aria-label',
  'legende',
])

export function traduireTexteVitrine(texte: string, langue: Langue): string {
  if (langue === 'fr') return texte
  const cle = texte.replace(/\s+/gu, ' ').trim()
  const traduit = CONTENU_TRADUIT[cle]?.[langue]
  if (traduit === undefined) return texte
  return (texte.match(/^\s*/u)?.[0] ?? '') + traduit + (texte.match(/\s*$/u)?.[0] ?? '')
}

export function localiserVitrine(noeud: ReactNode, langue: Langue): ReactNode {
  if (langue === 'fr') return noeud
  if (typeof noeud === 'string') return traduireTexteVitrine(noeud, langue)
  if (Array.isArray(noeud))
    return Children.map(noeud, (enfant: ReactNode) => localiserVitrine(enfant, langue))
  if (!isValidElement<Record<string, unknown>>(noeud)) return noeud
  const props: Record<string, unknown> = {}
  for (const [cle, valeur] of Object.entries(noeud.props)) {
    if (PROPS_TEXTE.has(cle) && typeof valeur !== 'function')
      props[cle] = localiserVitrine(valeur as ReactNode, langue)
    if (cle === 'visuel' && valeur !== null && typeof valeur === 'object') {
      const visuel = valeur as Record<string, unknown>
      props[cle] = {
        ...visuel,
        alt: typeof visuel.alt === 'string' ? traduireTexteVitrine(visuel.alt, langue) : visuel.alt,
        legende: localiserVitrine(visuel.legende as ReactNode, langue),
      }
    }
    if (cle === 'pastilles' && Array.isArray(valeur))
      props[cle] = valeur.map((p: Record<string, unknown>) => ({
        ...p,
        libelle:
          typeof p.libelle === 'string' ? traduireTexteVitrine(p.libelle, langue) : p.libelle,
      }))
  }
  return cloneElement(noeud, props)
}

export function useLocaliserVitrine(): (noeud: ReactNode) => ReactNode {
  const langue = useLangueVitrine()
  return (noeud) => localiserVitrine(noeud, langue)
}
